// contexts/PublisherInsightsContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useProgress } from './ProgressContext';

const PublisherInsightsContext = createContext(null);

export const usePublisherInsights = () => {
    const context = useContext(PublisherInsightsContext);
    if (!context) {
        throw new Error('usePublisherInsights must be used within a PublisherInsightsProvider');
    }
    return context;
};

export const PublisherInsightsProvider = ({ children }) => {
    const { startProgress, updateProgress, completeProgress } = useProgress();
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchInsights = useCallback(async () => {
        // If we already have insights, don't fetch again
        if (insights) return;

        setLoading(true);
        setError(null);
        startProgress(100, 'Loading publisher insights...');

        try {
            // Fetch all required data
            const response = await fetch('/analytics/api/publisher-insights');
            const data = await response.json();

            if (data.error) {
                throw new Error(data.error);
            }

            updateProgress(50, 'Processing publisher data...');

            setInsights(data);
            completeProgress();
        } catch (error) {
            console.error('Error fetching publisher insights:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [insights, startProgress, updateProgress, completeProgress]);

    const clearInsights = useCallback(() => {
        setInsights(null);
        setError(null);
    }, []);

    return (
        <PublisherInsightsContext.Provider value={{
            insights,
            loading,
            error,
            fetchInsights,
            clearInsights
        }}>
            {children}
        </PublisherInsightsContext.Provider>
    );
};